import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { LoginCredentials } from '../types/admin';
import { Logo } from './Logo';
import { ForgotPasswordModal } from './ForgotPasswordModal';
import './Login.css';

export const UnifiedLogin: React.FC = () => {
	const [credentials, setCredentials] = useState<LoginCredentials>({
		username: '',
		password: '',
	});
	const [error, setError] = useState<string>('');
	const [isLoading, setIsLoading] = useState(false);
	const [showLoginForm, setShowLoginForm] = useState(false);
	const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
	const { login } = useAuth();

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError('');
		setIsLoading(true);

		try {
			await login(credentials);
		} catch (err) {
			console.log(err);

			setError(err instanceof Error ? err.message : 'Greška pri prijavljivanju');
		} finally {
			setIsLoading(false);
		}
	};

	const handleInputChange = (field: keyof LoginCredentials, value: string) => {
		setCredentials((prev) => ({ ...prev, [field]: value }));
		if (error) setError('');
	};

	if (showLoginForm) {
		return (
			<div className='login-container'>
				<div className='login-card'>
					<div className='login-header'>
						<Logo size='large' />
						{/* <h1>Centar za zaštitu žrtava trgovine ljudima</h1> */}
						<p>Prijavite se za pristup aplikaciji</p>
					</div>

					<form onSubmit={handleSubmit} className='login-form'>
						{error && <div className='error-message'>{error}</div>}

						<div className='form-group'>
							<label htmlFor='username'>Korisničko ime</label>
							<input type='text' id='username' value={credentials.username} onChange={(e) => handleInputChange('username', e.target.value)} required disabled={isLoading} placeholder='Unesite korisničko ime' />
						</div>

						<div className='form-group'>
							<label htmlFor='password'>Lozinka</label>
							<input type='password' id='password' value={credentials.password} onChange={(e) => handleInputChange('password', e.target.value)} required disabled={isLoading} placeholder='Unesite lozinku' />
						</div>

						<button type='submit' className='login-button' disabled={isLoading}>
							{isLoading ? 'Prijavljivanje...' : 'Prijavite se'}
						</button>

						<div className='login-links'>
							<button type='button' className='forgot-password-link' onClick={() => setShowForgotPasswordModal(true)} disabled={isLoading}>
								Zaboravili ste lozinku?
							</button>
						</div>

						<button type='button' onClick={() => setShowLoginForm(false)} className='back-button' disabled={isLoading}>
							Nazad na informacije
						</button>
					</form>

					<ForgotPasswordModal isOpen={showForgotPasswordModal} onClose={() => setShowForgotPasswordModal(false)} />
				</div>
			</div>
		);
	}

	return (
		<div className='welcome-container'>
			<div className='welcome-content'>
				<div className='center-header'>
					<Logo size='large' />
					<h1>O Centru za zaštitu žrtava trgovine ljudima</h1>
				</div>

				<div className='login-actions'>
					<button onClick={() => setShowLoginForm(true)} className='main-login-button'>
						Prijavite se za pristup aplikaciji
					</button>
				</div>

				<div className='center-info'>
					<p>Ova aplikacija služi da vam pomogne da prepoznate trgovinu ljudima.</p>

					<p>Napominjemo da je aplikacija samo pomoćni alat, te da ona ne može sa potpunom sigurnošću da garantuje da se radi, odnosno da se ne radi o trgovini ljudima. Čak i u slučajevima kada nisu prepoznati indikatori trgovine ljudima, ako sumnjate da se radi o krivičnom delu, možete se konsultovati sa policijom. </p>
					<div className='info-section'>
						<h3>Ko može biti žrtva trgovine ljudima?</h3>
						<p>
							Bilo ko – neko iz vašeg komšiluka, škole, iz vaše porodice, ili čak i vi. Ovom aplikacijom moći ćete da testirate uočene signale i da proverite kolika je verovatnoća da je neka osoba žrtva trgovine ljudima. Potrebno je da, nakon što se ulogujete, izaberete opciju „nova prijava“. Nakon toga će vam se otvoriti lista indikatora (pokazatelja), koji su zapravo rečenice
							kojima se opisuje stanje u kome se nalazi osoba za koju sumnjate da je žrtva. Pročitajte ovu listu i kliknite na one indikatore koje ste prepoznali. Kada završite, dobićete rezultat u kome će biti naveden stepen rizika od trgovine ljudima, kao i uputstvo kako da postupate.
						</p>
					</div>

					<div className='info-section'>
						<h3>O trgovini ljudima </h3>
						<p>Trgovina ljudima je krivično delo koje prema Krivičnom zakoniku Republike Srbije spada u zločine protiv čovečnosti, za koje je zaprećena višegodišnja zatvorska kazna.Ona podrazumeva eksploataciju osobe ili njeno vrbovanje sa ciljem eksploatacije.</p>
						<p>Najčešči oblici trgovine ljudima su:</p>
						<ul>
							<li>Radna eksploatacija</li>
							<li>Seksualna eksploatacija</li>
							<li>Prinuda na prosjačenje</li>
							<li>Prinuda na vršenje krivičnih djela</li>
							<li>Prinudni brakovi</li>
							<li>Oduzimanje organa</li>
							<li>Prinuda na učestvovanje u ratnim sukobima</li>
						</ul>
						<p>Osobe u postupku identifikacije imaju sva prava koja su zagarantovana formalno identifikovanim žrtvama trgovine ljudima, sve dok traje postupak.</p>
					</div>
				</div>
				<div className='info-section'>
					<h3>O Centru za zaštitu žrtava trgovine ljudima</h3>
					<p>Centar za zaštitu žrtava trgovine ljudima je ustanova socijalne zaštite koja identifikuje žrtve trgovine ljudima, obezbeđuje im savetodavnu, psihološku podršku, podršku za učešće u krovičnom postupku, siguran smeštaj i pomaže im u oporavku.</p>
				</div>

				<div className='info-section contact-section'>
					<h3>Kako da nas kontaktirate?</h3>
					<p>
						Prijavu možete poslati direktno putem ove aplikacije, a za sve dodatne konsultacije možete da nas kontaktirate na telefon: <a href='tel:+38163610590'>+38163610590</a> ili mejl adresu: <a href='mailto:centar@centarzztlj.rs'>centar@centarzztlj.rs</a>
					</p>
				</div>
			</div>
		</div>
	);
};
